import React from "react"
import Filtering, { Filter, FilterKey, FilterType } from "./Filtering/Filtering"
import { useQuery } from "../Common/Hooks/useQuery"
import { SortsContext } from "../Common/Hooks/useSorts"
import QueryString from "../Common/QueryString"
import { Play, Player } from "../Common/SI"
import { Tables } from "./Tables/Tables"
import { Filters } from "./Filtering/Filters"
import { PlayCards } from "./PlayCards"
import { useNavigate } from "react-router-dom"
import Sorting, { Sort } from "./Sorting"
import Parsing, { ParseResults } from "../Common/Parsing"
import { Message, Messages } from "../Common/Messages"
import { Charts } from "./Charts/Charts"
import { FiltersContext } from "../Common/Hooks/useFilters"
import { Header } from "../Common/Header"

import './Stats.css'
import { ModalContext } from "../Common/Hooks/useModal"

export function Stats(): JSX.Element {
	const [difficulties, setDifficulties] = React.useState<number[]>([])
	const [error, setError] = React.useState<string | null>(null)
	const [filters, setFilters] = React.useState<Filter[] | null>(null)
	const [filteredPlays, setFilteredPlays] = React.useState<Play[]>([])
	const [layouts, setLayouts] = React.useState<string[]>([])
	const [locations, setLocations] = React.useState<string[]>([])
	const [maxSpirits, setMaxSpirits] = React.useState<number>(0)
	const [messages, setMessages] = React.useState<Message[]>([])
	const [modalBackgroundVisible, setModalBackgroundVisible] = React.useState<boolean>(false)
	const [players, setPlayers] = React.useState<Player[]>([])
	const [plays, setPlays] = React.useState<Play[]>([])
	const [sorts, setSorts] = React.useState<Map<string, Sort>>(Sorting.getDefaultSorts())
	const query = useQuery()
	const navigate = useNavigate()

	React.useEffect(() => {
		// Initial load
		setMessages([
			{
				type: 'info',
				text: 'Loading...',
			}
		])
		var xhr = new XMLHttpRequest()
		xhr.open('GET', 'https://s3.eu-west-2.amazonaws.com/com.timjeanes.wideopen/bgstats.json')
		xhr.onload = () => {
			if (xhr.status !== 200) {
				setError('Unable to read file')
			}

			try {

				const results: ParseResults = Parsing.processData(xhr.responseText)

				setMessages(results.messages)
				setPlays(results.plays)
				setDifficulties(results.difficulties)
				setMaxSpirits(results.maxSpirits)
				setLayouts(results.layouts)
				setLocations(results.locations)

			} catch (error: unknown) {
				setError(`${error}`)
			}
		}

		xhr.onerror = () => {
			setError('Unable to read file')
		}

		xhr.send()
	}, [])

	React.useEffect(() => {
		// Filters changed: update query string
		if (!filters) {
			// Filters aren't initialized yet - get out so we don't change the query string before we've read it
			return
		}

		const queryString = QueryString.make(filters)
		navigate(queryString)
	}, [filters, navigate])

	React.useEffect(() => {
		// Query string changed: update filters
		const filters = QueryString.getFilters(query)
		setFilters(filters)
	}, [query])

	React.useEffect(() => {
		// Filters or plays changed: update filtered plays
		if (filters) {
			setFilteredPlays(Filtering.filterPlays(plays, filters))
		}
	}, [filters, plays])

	React.useEffect(() => {
		// Plays changed: update list of players
		const players: Player[] = []
		for (const play of plays) {
			for (const player of play.players) {
				if (!players.some(p => p.name === player.name)) {
					players.push(player)
				}
			}
		}
		players.sort((a, b) => (a.playCount !== b.playCount ? b.playCount - a.playCount : a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
		setPlayers(players)
	}, [plays])

	function addFilter(key: FilterKey, value: string): void {
		if (!filters) {
			return
		}

		const existingFilter = filters.find(f => f.key === key)
		let filter: Filter
		if (existingFilter) {
			filter = { ...existingFilter }
		} else {
			filter = {
				canChangeType: Filtering.canChangeType(key),
				key: key,
				type: Filtering.initialType(key),
				values: [],
			}
		}

		if (['outcomes'].indexOf(filter.key) !== -1) {
			// These can only filter on one value
			filter.values = [value]
		} else if (filter.key === 'dateRange') {
			filter.values = [value.substring(0, 10), value.substring(0, 10)]
		} else if (!filter.values.some(v => v === value)) {
			filter.values.push(value)
		}

		setFilters([
			...filters.filter(f => f.key !== key),
			filter,
		])
	}

	function modalBackgroundClick(): void {
		setModalBackgroundVisible(false)
	}

	function setFilter(key: FilterKey, values: string[]): void {
		if (!filters) {
			return
		}

		const filter: Filter = {
			canChangeType: Filtering.canChangeType(key),
			key: key,
			type: Filtering.initialType(key),
			values,
		}

		setFilters([
			...filters.filter(f => f.key !== key),
			filter,
		])
	}

	function removeFilter(key: FilterKey): void {
		if (!filters) {
			return
		}

		setFilters([
			...filters.filter(f => f.key !== key),
		])
	}

	function removeFilterValue(key: FilterKey, value: string): void {
		if (!filters) {
			return
		}

		const existingFilter = filters.find(f => f.key === key)
		if (!existingFilter) {
			return
		}

		if (existingFilter.values.length === 1) {
			setFilters([
				...filters.filter(f => f.key !== key),
			])
			return
		}

		const newFilter: Filter = {
			...existingFilter,
			values: [
				...existingFilter.values.filter(v => v !== value),
			]
		}

		setFilters([
			...filters.filter(f => f.key !== key),
			newFilter,
		])
	}

	function rotateFilterKey(key: FilterKey): void {
		if (!filters) {
			return
		}

		const existingFilter = filters.find(f => f.key === key)
		if (!existingFilter) {
			return
		}

		const order: FilterType[] = ['all', 'exact', 'any']

		const newType = order[(order.indexOf(existingFilter.type) + 1) % order.length]

		const newFilter: Filter = {
			...existingFilter,
			type: newType,
		}

		setFilters([
			...filters.filter(f => f.key !== key),
			newFilter,
		])
	}

	function showModalBackground(): void {
		setModalBackgroundVisible(true)
	}

	function toggleSort(table: string, field: string, defaultAscending?: boolean, canClear?: boolean): void {

		const newSorts = new Map(sorts)
		const sort = sorts.get(table)

		if (!sort || sort.field !== field) {
			newSorts.set(table, {
				field,
				ascending: !!defaultAscending,
			})
		} else if (canClear && sort.ascending === !defaultAscending) {
			newSorts.delete(table)
		} else {
			newSorts.set(table, {
				field,
				ascending: !sort.ascending,
			})
		}

		setSorts(newSorts)
	}

	if (error) {
		return <div id="error">{error}</div>
	}

	return (
		<ModalContext.Provider value={{ modalBackgroundVisible, showModalBackground }}>
			<FiltersContext.Provider value={{ filters, addFilter, removeFilter, removeFilterValue, rotateFilterKey, setFilter }}>
				<SortsContext.Provider value={{ sorts, toggleSort }}>
					<Header>
						<React.Fragment>
							<Messages messages={messages} />
							<div className='filter-shortcuts'>
								{
									!!filters?.length &&
									<div>
										<button onClick={(e) => {
											e.preventDefault()
											setFilters([])
										}}>Clear filters</button>
									</div>
								}
								{
									!filters?.length &&
									<div>
										<button onClick={(e) => {
											e.preventDefault()
											setFilters([
												{
													key: 'players',
													canChangeType: true,
													type: 'exact',
													values: ['Tim Jeanes', 'Andy Billington', 'Matthew Evans'],
												},
											])
										}}>Tim, Andy, Matthew</button>
										<button onClick={(e) => {
											e.preventDefault()
											setFilters([
												{
													key: 'players',
													canChangeType: true,
													type: 'exact',
													values: ['Tim Jeanes'],
												},
												{
													key: 'spiritCounts',
													canChangeType: false,
													type: 'any',
													values: ['2'],
												},
											])
										}}>2-handed solo</button>
									</div>
								}
							</div>
							{filters && <Filters filters={filters} />}
						</React.Fragment>
					</Header>
					{modalBackgroundVisible && (
						<div
							className="callout-background"
							onClick={modalBackgroundClick}></div>
					)}
					{plays.length > 0 &&
						<section className="main">
							<Tables
								difficulties={difficulties}
								layouts={layouts}
								locations={locations}
								maxSpirits={maxSpirits}
								players={players}
								plays={filteredPlays}
							/>
							<Charts
								plays={filteredPlays}
							/>
							<PlayCards plays={filteredPlays} />
						</section>
					}
				</SortsContext.Provider>
			</FiltersContext.Provider>
		</ModalContext.Provider>
	)
}
